import React, { useState } from "react";
import { Avatar } from "../shared/Avatar";
import { CustomIcon } from "../shared/CustomIcon";
import UserForm from "./UserForm";
import { apiAdminCreateUser, apiAdminUpdateUser, apiAdminDeleteUser } from "./api";
import { kcFetch, API_BASE, authHeaders } from "../../lib/apiClient";

function roleLabel(role) {
  if (role === "superadmin")    return "Super Admin";
  if (role === "limited_admin") return "Limited Admin";
  if (role === "pastor")        return "Pastor";
  return "Team Member";
}
function roleBadgeClass(role) {
  if (role === "superadmin")    return "badge-gold";
  if (role === "limited_admin") return "badge-blue";
  if (role === "pastor")        return "badge-green";
  return "badge-charcoal";
}

// ============================================================
// UsersPage — Users & Roles admin grid. Extracted verbatim from
// KingdomConnect.jsx — no behaviour changes.
// ============================================================
export default function UsersPage({ currentUser, users, setUsers, members, followups, addAudit, persist }) {
  const [showModal, setShowModal] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [resetBusy, setResetBusy] = useState(false);
  const [resetMsg, setResetMsg] = useState("");

  const saveUser = async (data) => {
    setError(""); setBusy(true);
    try {
      if (data.id && users.find(u => u.id === data.id)) {
        // UPDATE
        const payload = {
          username: data.username,
          name: data.name,
          role: data.role,
          email: data.email || "",
          memberId: data.memberId || "",
          assignedMembers: data.assignedMembers || [],
          sessionTimeout: data.sessionTimeout || 30,
          photo: data.photo ?? null,
          attendanceAccess: data.role === "team_member" ? !!data.attendanceAccess : null,
        };
        if (data.password && data.password.trim()) payload.password = data.password;
        const fresh = await apiAdminUpdateUser(data.id, payload);
        const updated = users.map(u => u.id === data.id ? { ...u, ...fresh } : u);
        setUsers(updated);
        addAudit(currentUser.id, currentUser.name, "Updated user", `Updated ${fresh.name}${data.password ? " (incl. password)" : ""}`);
      } else {
        // CREATE
        if (!data.password || !data.password.trim()) {
          throw new Error("Password is required for new users");
        }
        const payload = {
          username: data.username,
          password: data.password,
          name: data.name,
          role: data.role,
          email: data.email || "",
          memberId: data.memberId || "",
          assignedMembers: data.assignedMembers || [],
          sessionTimeout: data.sessionTimeout || 30,
          photo: data.photo ?? null,
          attendanceAccess: data.role === "team_member" ? !!data.attendanceAccess : null,
          detailedGivingAccess: data.role === "pastor" ? !!data.detailedGivingAccess : null,
        };
        const fresh = await apiAdminCreateUser(payload);
        const updated = [...users, fresh];
        setUsers(updated);
        addAudit(currentUser.id, currentUser.name, "Created user", `Added ${fresh.name} (${fresh.role}) — can log in now`);
      }
      setShowModal(false); setEditUser(null);
    } catch (e) {
      setError(e.message || "Could not save user");
    } finally { setBusy(false); }
  };

  const deleteUser = async (id) => {
    if (id === currentUser.id) { alert("You cannot delete your own account."); return; }
    const u = users.find(x => x.id === id);
    if (!window.confirm(`Delete user ${u?.name}? This action cannot be undone.`)) return;
    setBusy(true); setError("");
    try {
      await apiAdminDeleteUser(id);
      const updated = users.filter(x => x.id !== id);
      setUsers(updated);
      addAudit(currentUser.id, currentUser.name, "Deleted user", `Removed ${u?.name}`);
    } catch (e) {
      alert(e.message || "Could not delete user");
    } finally { setBusy(false); }
  };

  return (
    <div>
      <div className="flex justify-between mb-20">
        <div style={{ fontSize: 13, color: "var(--text3)" }}>{users.length} users</div>
        <button className="btn btn-gold" onClick={() => { setEditUser(null); setShowModal(true); }}>+ Create User</button>
      </div>
      <div className="card">
        <div className="card-header"><div className="card-title">Users &amp; Roles</div></div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Name</th><th>Username</th><th>Email</th><th>Role</th>
                <th>Assigned</th><th>Follow-Ups</th><th></th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => {
                const fus = followups.filter(f => f.teamMemberId === u.id).length;
                return (
                  <tr key={u.id}>
                    <td>
                      <div className="flex items-center gap-10">
                        <Avatar name={u.name} photo={u.photo} memberId={u.memberId} style={{ fontSize: 11 }} />
                        <span style={{ fontWeight: 500 }}>{u.name}</span>
                        {u.isSeed && <span className="badge badge-gray" style={{ marginLeft: 6, fontSize: 9 }}>SEED</span>}
                      </div>
                    </td>
                    <td><span style={{ fontFamily: "monospace", fontSize: 12, background: "var(--cream)", padding: "2px 8px", borderRadius: 4 }}>{u.username}</span></td>
                    <td style={{ fontSize: 12, color: "var(--text2)" }}>{u.email || "—"}</td>
                    <td><span className={`badge ${roleBadgeClass(u.role)}`}>{roleLabel(u.role)}</span></td>
                    <td>{(u.role === "team_member" || u.role === "pastor") ? (u.assignedMembers?.length || 0) : "All"}</td>
                    <td>{fus}</td>
                    <td>
                      <div className="flex gap-8">
                        <button className="btn btn-sm btn-outline" onClick={() => { setEditUser(u); setShowModal(true); }}>Edit</button>
                        {u.id !== currentUser.id && <button className="btn btn-sm btn-danger" onClick={() => deleteUser(u.id)}>Del</button>}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      {showModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && !busy && setShowModal(false)}>
          <div className="modal" style={{ maxWidth: 520 }}>
            <div className="modal-header">
              <div className="modal-title">{editUser ? "Edit User" : "Create User"}</div>
              <button className="modal-close" onClick={() => !busy && setShowModal(false)}>×</button>
            </div>
            {error && <div className="login-error" data-testid="user-form-error" style={{ margin: "14px 24px 0" }}>{error}</div>}
            <UserForm user={editUser} members={members} onSave={saveUser} onClose={() => setShowModal(false)} busy={busy} />
          </div>
        </div>
      )}

      {currentUser?.role === "superadmin" && (
        <div
          data-testid="danger-zone"
          style={{
            marginTop: 36, padding: 22, borderRadius: 12,
            background: "rgba(255,107,107,0.04)",
            border: "2px solid rgba(255,107,107,0.45)",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
            <span style={{ fontSize: 20, display: "inline-flex" }}><CustomIcon name="warning" size={20} /></span>
            <div style={{ fontFamily: "'Cinzel',serif", fontSize: 14, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "#ff6b6b" }}>
              Danger Zone
            </div>
          </div>
          <p style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 12, color: "var(--kc-sub)", lineHeight: 1.5, marginBottom: 14 }}>
            Reset Demo Data permanently deletes <strong>all seed users</strong> (accounts marked <code>isSeed: true</code>).
            This is intended for production go-live after you have created at least one real Super Admin.
            This cannot be undone.
          </p>
          <button
            className="btn"
            data-testid="reset-demo-data-btn"
            disabled={resetBusy || currentUser?.isSeed}
            title={currentUser?.isSeed ? "Log in as a non-seed Super Admin first" : ""}
            onClick={async () => {
              if (currentUser?.isSeed) { alert("Create a real (non-seed) Super Admin first, log in as them, then run this."); return; }
              if (!window.confirm("Permanently delete every seed user? This cannot be undone.")) return;
              setResetBusy(true); setResetMsg("");
              try {
                const res = await kcFetch(`${API_BASE}/admin/reset-demo`, {
                  method: "POST",
                  headers: { ...authHeaders() },
                });
                if (!res.ok) {
                  const err = await res.json().catch(() => ({}));
                  throw new Error(err.detail || `HTTP ${res.status}`);
                }
                const data = await res.json();
                setResetMsg(`Deleted ${data.deletedCount} seed user(s). ${data.remaining} real user(s) remain.`);
                const remaining = users.filter(u => !u.isSeed);
                setUsers(remaining);
                addAudit(currentUser.id, currentUser.name, "Reset demo data", `Removed ${data.deletedCount} seed users`);
              } catch (e) {
                setResetMsg(`Error: ${e.message || "failed"}`);
              } finally { setResetBusy(false); }
            }}
            style={{
              background: currentUser?.isSeed ? "rgba(255,107,107,0.15)" : "#ff6b6b",
              color: currentUser?.isSeed ? "rgba(255,107,107,0.55)" : "#FFFFFF",
              border: "none", fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase",
              opacity: currentUser?.isSeed || resetBusy ? 0.6 : 1,
              cursor: currentUser?.isSeed ? "not-allowed" : "pointer",
            }}
          >
            {resetBusy ? "Deleting…" : <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><CustomIcon name="trash" size={14} /> Reset Demo Data</span>}
          </button>
          {resetMsg && (
            <div data-testid="reset-demo-result" style={{ marginTop: 12, fontSize: 12, color: resetMsg.startsWith("Error") ? "#ff6b6b" : "#4ade80", fontFamily: "'Montserrat',sans-serif" }}>
              {resetMsg}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
